/**
 * Activity Feed Redux Slice
 * Manages activity log and audit trail state
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ActivityLog,
  ActivityFeedParams,
  EntityType,
} from '../../api/types/ActivityTypes';
import { activityApi } from '../../api/endpoints/activityApi';

// ==================== State Interface ====================

interface ActivityState {
  // Board activity feeds (keyed by boardId)
  boardActivities: { [boardId: string]: ActivityLog[] };
  boardActivityTotal: { [boardId: string]: number };
  // Workspace activity feeds (keyed by workspaceId)
  workspaceActivities: { [workspaceId: string]: ActivityLog[] };
  workspaceActivityTotal: { [workspaceId: string]: number };
  workspaceActivityLoading: boolean;
  workspaceActivityError: string | null;

  // User activity feed
  userActivities: ActivityLog[];
  userActivityTotal: number;
  userActivityLoading: boolean;
  userActivityError: string | null;

  // Recent activity (last 24 hours)
  recentActivities: { [workspaceId: string]: ActivityLog[] };
  recentActivityLoading: boolean;
  recentActivityError: string | null;

  // Entity-specific activity
  entityActivities: { [key: string]: ActivityLog[] }; // key = "entityType:entityId"
  entityActivityLoading: boolean;
  entityActivityError: string | null;

  // Pagination
  currentPage: { [workspaceId: string]: number };
  pageSize: number;
}

const initialState: ActivityState = {
  boardActivities: {},
  boardActivityTotal: {},
  workspaceActivities: {},
  workspaceActivityTotal: {},
  workspaceActivityLoading: false,
  workspaceActivityError: null,
  userActivities: [],
  userActivityTotal: 0,
  userActivityLoading: false,
  userActivityError: null,
  recentActivities: {},
  recentActivityLoading: false,
  recentActivityError: null,
  entityActivities: {},
  entityActivityLoading: false,
  entityActivityError: null,
  currentPage: {},
  pageSize: 50,
};

// ==================== Async Thunks ====================

/**
 * Fetch board activity feed
 */
export const fetchBoardActivityFeed = createAsyncThunk(
  'activity/fetchBoardActivityFeed',
  async ({
    boardId,
    params,
  }: {
    boardId: string;
    params?: ActivityFeedParams;
  }) => {
    const response = await activityApi.getBoardActivityFeed(boardId, params);
    return { boardId, ...response };
  },
);

/**
 * Fetch workspace activity feed
 */
export const fetchWorkspaceActivityFeed = createAsyncThunk(
  'activity/fetchWorkspaceActivityFeed',
  async ({
    workspaceId,
    params,
  }: {
    workspaceId: string;
    params?: ActivityFeedParams;
  }) => {
    const response = await activityApi.getWorkspaceActivityFeed(
      workspaceId,
      params,
    );
    return { workspaceId, ...response };
  },
);

/**
 * Fetch user activity feed
 */
export const fetchUserActivityFeed = createAsyncThunk(
  'activity/fetchUserActivityFeed',
  async (params?: ActivityFeedParams) => {
    const response = await activityApi.getUserActivityFeed(params);
    return response;
  },
);

/**
 * Fetch recent activity
 */
export const fetchRecentActivity = createAsyncThunk(
  'activity/fetchRecentActivity',
  async ({ workspaceId, hours }: { workspaceId: string; hours?: number }) => {
    const activities = await activityApi.getRecentActivity(workspaceId, hours);
    return { workspaceId, activities };
  },
);

/**
 * Fetch entity-specific activity
 */
export const fetchEntityActivity = createAsyncThunk(
  'activity/fetchEntityActivity',
  async ({
    entityType,
    entityId,
    limit,
  }: {
    entityType: EntityType;
    entityId: string;
    limit?: number;
  }) => {
    const activities = await activityApi.getEntityActivity(
      entityType,
      entityId,
      limit,
    );
    return { entityType, entityId, activities };
  },
);

// ==================== Slice ====================

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    // Clear all activity data
    clearActivityData(state) {
      state.workspaceActivities = {};
      state.workspaceActivityTotal = {};
      state.userActivities = [];
      state.userActivityTotal = 0;
      state.recentActivities = {};
      state.entityActivities = {};
    },

    // Clear errors
    clearErrors(state) {
      state.workspaceActivityError = null;
      state.userActivityError = null;
      state.recentActivityError = null;
      state.entityActivityError = null;
    },

    // Clear workspace activity
    clearWorkspaceActivity(state, action) {
      const workspaceId = action.payload;
      delete state.workspaceActivities[workspaceId];
      delete state.workspaceActivityTotal[workspaceId];
      delete state.recentActivities[workspaceId];
      delete state.currentPage[workspaceId];
    },

    // Set page size
    setPageSize(state, action) {
      state.pageSize = action.payload;
    },

    // Add new activity (for real-time updates)
    addActivity(state, action) {
      const activity: ActivityLog = action.payload;
      if (activity.workspaceId) {
        const workspaceId = activity.workspaceId;
        if (state.workspaceActivities[workspaceId]) {
          state.workspaceActivities[workspaceId].unshift(activity);
          state.workspaceActivityTotal[workspaceId] += 1;
        }
        if (state.recentActivities[workspaceId]) {
          state.recentActivities[workspaceId].unshift(activity);
        }
      }
      if (activity.userId) {
        state.userActivities.unshift(activity);
        state.userActivityTotal += 1;
      }
    },
  },
  extraReducers: (builder) => {
    // ==================== Board Activity Feed ====================
    builder
      .addCase(fetchBoardActivityFeed.pending, (state) => {
        state.workspaceActivityLoading = true;
        state.workspaceActivityError = null;
      })
      .addCase(fetchBoardActivityFeed.fulfilled, (state, action) => {
        state.workspaceActivityLoading = false;
        const { boardId, activities, total } = action.payload;
        const offset = action.meta.arg.params?.offset || 0;
        state.boardActivities[boardId] =
          offset > 0 ? [...(state.boardActivities[boardId] || []), ...activities] : activities;
        state.boardActivityTotal[boardId] = total;
      })
      .addCase(fetchBoardActivityFeed.rejected, (state, action) => {
        state.workspaceActivityLoading = false;
        state.workspaceActivityError =
          action.error.message || 'Failed to fetch board activity feed';
      });

    // ==================== Workspace Activity Feed ====================
    builder
      .addCase(fetchWorkspaceActivityFeed.pending, (state) => {
        state.workspaceActivityLoading = true;
        state.workspaceActivityError = null;
      })
      .addCase(fetchWorkspaceActivityFeed.fulfilled, (state, action) => {
        state.workspaceActivityLoading = false;
        const { workspaceId, activities, total } = action.payload;
        const offset = action.meta.arg.params?.offset || 0;
        state.workspaceActivities[workspaceId] =
          offset > 0
            ? [...(state.workspaceActivities[workspaceId] || []), ...activities]
            : activities;
        state.workspaceActivityTotal[workspaceId] = total;
      })
      .addCase(fetchWorkspaceActivityFeed.rejected, (state, action) => {
        state.workspaceActivityLoading = false;
        state.workspaceActivityError =
          action.error.message || 'Failed to fetch workspace activity feed';
      });

    // ==================== User Activity Feed ====================
    builder
      .addCase(fetchUserActivityFeed.pending, (state) => {
        state.userActivityLoading = true;
        state.userActivityError = null;
      })
      .addCase(fetchUserActivityFeed.fulfilled, (state, action) => {
        state.userActivityLoading = false;
        const { activities, total } = action.payload;
        state.userActivities = activities;
        state.userActivityTotal = total;
      })
      .addCase(fetchUserActivityFeed.rejected, (state, action) => {
        state.userActivityLoading = false;
        state.userActivityError =
          action.error.message || 'Failed to fetch user activity feed';
      });

    // ==================== Recent Activity ====================
    builder
      .addCase(fetchRecentActivity.pending, (state) => {
        state.recentActivityLoading = true;
        state.recentActivityError = null;
      })
      .addCase(fetchRecentActivity.fulfilled, (state, action) => {
        state.recentActivityLoading = false;
        const { workspaceId, activities } = action.payload;
        state.recentActivities[workspaceId] = activities;
      })
      .addCase(fetchRecentActivity.rejected, (state, action) => {
        state.recentActivityLoading = false;
        state.recentActivityError =
          action.error.message || 'Failed to fetch recent activity';
      });

    // ==================== Entity Activity ====================
    builder
      .addCase(fetchEntityActivity.pending, (state) => {
        state.entityActivityLoading = true;
        state.entityActivityError = null;
      })
      .addCase(fetchEntityActivity.fulfilled, (state, action) => {
        state.entityActivityLoading = false;
        const { entityType, entityId, activities } = action.payload;
        const key = `${entityType}:${entityId}`;
        state.entityActivities[key] = activities;
      })
      .addCase(fetchEntityActivity.rejected, (state, action) => {
        state.entityActivityLoading = false;
        state.entityActivityError =
          action.error.message || 'Failed to fetch entity activity';
      });
  },
});

// ==================== Exports ====================

export const {
  clearActivityData,
  clearErrors,
  clearWorkspaceActivity,
  setPageSize,
  addActivity,
} = activitySlice.actions;

export default activitySlice.reducer;
